import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import { ClienteService } from '../../../../services/ClienteService';
import Http from '../../../../Http';

export const ClienteFactoraje = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;

  const [estados, setEstados] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [colonias, setColonias] = useState([]);
  const [regimenesFiscales, setRegimenesFiscales] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchOptions = async () => {
      try {
        const opts = await ClienteService.obtenerCatalogoOptions();
        if (mounted && opts) {
          if (opts.estados) {
            setEstados(
              opts.estados.map((e) => ({
                value: String(e.id),
                label: e.estado,
              }))
            );
          }
          if (opts.regimenes_fiscales) {
            setRegimenesFiscales(
              opts.regimenes_fiscales.map((r) => ({
                value: String(r.id),
                label: r.label || `${r.cfdi_regimen_clave} - ${r.cfdi_regimen_nombre}`,
              }))
            );
          }
        }
      } catch (err) {
        console.error('Error cargando catálogos de factoraje', err);
      }
    };
    fetchOptions();
    return () => {
      mounted = false;
    };
  }, []);

  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const data = await ClienteService.obtenerDetalleCompleto(clientId);
      if (!data) return null;

      if (data.cliente_factoraje_cp && String(data.cliente_factoraje_cp).length === 5) {
        try {
          const res = await Http.get(`cliente/form-factoraje?cp=${data.cliente_factoraje_cp}`);
          if (res?.data?.direccion) {
            const { direccion } = res.data;
            if (direccion.colonias) {
              setColonias(direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia })));
            }
            if (direccion.ciudades) {
              setCiudades(direccion.ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
            }
          }
        } catch (e) {
          console.error('Error al precargar colonias de factoraje', e);
        }
      }

      return {
        cliente_factoraje_empresa: data.cliente_factoraje_empresa || '',
        cliente_factoraje_rfc: data.cliente_factoraje_rfc || '',
        cliente_factoraje_id_regimen_fiscal: data.cliente_factoraje_id_regimen_fiscal ? String(data.cliente_factoraje_id_regimen_fiscal) : '',
        cliente_factoraje_cp: data.cliente_factoraje_cp || '',
        cliente_factoraje_id_estado: data.cliente_factoraje_id_estado ? String(data.cliente_factoraje_id_estado) : '',
        cliente_factoraje_id_ciudad: data.cliente_factoraje_id_ciudad ? String(data.cliente_factoraje_id_ciudad) : '',
        cliente_factoraje_id_colonia: data.cliente_factoraje_id_colonia ? String(data.cliente_factoraje_id_colonia) : '',
        cliente_factoraje_domicilio: data.cliente_factoraje_domicilio || '',
        cliente_factoraje_next: data.cliente_factoraje_next || '',
      };
    } catch (e) {
      console.error('Error al cargar datos de factoraje', e);
      return null;
    }
  }, [clientId]);

  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    const cpStr = String(val || '').trim();
    if (cpStr.length === 5) {
      try {
        const res = await Http.get(`cliente/form-factoraje?cp=${cpStr}`);
        if (res?.data?.direccion) {
          const { direccion } = res.data;
          if (direccion.colonias) {
            setColonias(direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia })));
          }
          if (direccion.ciudades) {
            setCiudades(direccion.ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
          }
          if (direccion.estado?.id) {
            setFieldValue('cliente_factoraje_id_estado', String(direccion.estado.id));
          }
          if (direccion.ciudad?.id) {
            setFieldValue('cliente_factoraje_id_ciudad', String(direccion.ciudad.id));
          }
        }
      } catch (err) {
        console.error('Error al consultar CP factoraje', err);
      }
    } else {
      setColonias([]);
      setFieldValue('cliente_factoraje_id_colonia', '');
    }
  }, []);

  const handleEstadoChange = useCallback(async (val, { setFieldValue }) => {
    if (val) {
      try {
        const res = await Http.get(`cliente/form-factoraje?e=${val}`);
        if (res?.data?.ciudades) {
          setCiudades(res.data.ciudades.map((c) => ({ value: String(c.id), label: c.ciudad })));
        }
      } catch (err) {
        console.error('Error al consultar ciudades por estado factoraje', err);
      }
    } else {
      setCiudades([]);
      setFieldValue('cliente_factoraje_id_ciudad', '');
    }
  }, []);

  const fields = useMemo(
    () => [
      {
        name: 'section_fiscales',
        label: 'Datos Fiscales de Factoraje',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_factoraje_empresa',
        label: 'Empresa / Razón Social Factoraje',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('La empresa de factoraje es requerida')],
        placeholder: 'Nombre o razón social',
      },
      {
        name: 'cliente_factoraje_rfc',
        label: 'RFC Factoraje',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('El RFC es requerido')],
        placeholder: '12 caracteres (persona moral)',
      },
      {
        name: 'cliente_factoraje_id_regimen_fiscal',
        label: 'Régimen Fiscal',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El régimen fiscal es requerido')],
        options: regimenesFiscales,
        placeholder: 'Seleccione régimen',
      },
      {
        name: 'section_domicilio',
        label: 'Domicilio de Factoraje',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_factoraje_cp',
        label: 'Código Postal',
        type: 'number',
        col: 3,
        required: true,
        validators: [Validators.required('El código postal es requerido')],
        helperText: '5 dígitos para autocompletar',
        onChange: handleCpChange,
      },
      {
        name: 'cliente_factoraje_id_colonia',
        label: 'Colonia',
        type: 'select',
        col: 5,
        required: true,
        validators: [Validators.required('La colonia es requerida')],
        options: colonias,
        placeholder: colonias.length ? 'Seleccione colonia' : 'Ingrese un CP válido',
      },
      {
        name: 'cliente_factoraje_domicilio',
        label: 'Calle / Domicilio',
        type: 'text',
        col: 4,
        required: true,
        validators: [Validators.required('La calle es requerida')],
        placeholder: 'Calle y número',
      },
      {
        name: 'cliente_factoraje_next',
        label: 'N. Exterior',
        type: 'text',
        col: 4,
        placeholder: 'Ext.',
      },
      {
        name: 'cliente_factoraje_id_estado',
        label: 'Estado',
        type: 'select',
        col: 4,
        required: true,
        validators: [Validators.required('El estado es requerido')],
        options: estados,
        onChange: handleEstadoChange,
      },
      {
        name: 'cliente_factoraje_id_ciudad',
        label: 'Delegación o Municipio',
        type: 'select',
        col: 4,
        required: true,
        validators: [Validators.required('El municipio es requerido')],
        options: ciudades,
        placeholder: ciudades.length ? 'Seleccione municipio' : 'Seleccione estado',
      },
    ],
    [regimenesFiscales, estados, ciudades, colonias, handleCpChange, handleEstadoChange]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        id: clientId,
        ...values,
      };
      return await ClienteService.guardar(payload);
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response);
      }
    },
    [onSave]
  );

  return (
    <div className="space-y-4">
      <CatalogForm
        title="Factoraje Financiero"
        subtitle="Datos de la empresa y domicilio fiscal asignado para operaciones de factoraje"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        submitButtonText="Guardar Datos de Factoraje"
      />
    </div>
  );
};

export default withRouter(ClienteFactoraje);
